import { Link } from "@tanstack/react-router";
import { IconArrowRight, IconBuilding } from "@tabler/icons-react";
import { Badge, Card, Divider, Group, Stack, Text, ThemeIcon } from "@/components/ui";
import type { BandejaAutorizacionItem } from "@/lib/types";
import { formatearMonto } from "@/shared/format/money";
import { etiquetaTipoSolicitud, etiquetaSubtipoSolicitud } from "@/lib/solicitud-labels";
import { formatFecha } from "@/shared/format/fecha";

/**
 * Card densa para mobile · número grande, badge de nivel, fecha,
 * organización y total. Pie inferior con CTA "Resolver".
 */
export function BandejaMobileCard({ it }: { it: BandejaAutorizacionItem }) {
  return (
    <Card padding="xs" className="overflow-hidden p-0" style={{ borderLeft: "3px solid var(--color-multiva-500)" }}>
      <div className="p-4">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={6} className="min-w-0 flex-1">
              <Text fw={700} ff="mono" size="md">
                {it.folio}
              </Text>
              <Group gap={4} wrap="wrap">
                <Badge size="sm" color="azulProfundo">
                  Nivel {it.nivelActual - 1}/{it.cantidadNiveles}
                </Badge>
                <Badge size="sm" color="slate" uppercase={false}>
                  {etiquetaTipoSolicitud(it.tipo)} · {etiquetaSubtipoSolicitud(it.subtipo)}
                </Badge>
              </Group>
            </Stack>
            <Stack gap={0} align="flex-end" className="shrink-0">
              <Text size="xs" c="dimmed" fw={600} ff="mono" className="tracking-wide" style={{ fontSize: 10 }}>
                {formatFecha(it.fechaUltimoEvento)}
              </Text>
              <Text size="xs" c="dimmed" ff="mono" style={{ fontSize: 10 }}>
                {new Date(it.fechaUltimoEvento).toLocaleTimeString("sv-SE", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
              </Text>
            </Stack>
          </Group>

          <Group gap="sm" wrap="nowrap" align="center">
            <ThemeIcon variant="filled" color="multiva" size="md">
              <IconBuilding size={16} stroke={1.6} />
            </ThemeIcon>
            <div className="min-w-0 flex-1">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="tracking-wide" style={{ fontSize: 10 }}>
                Organización
              </Text>
              <Text size="sm" fw={500} truncate>
                {it.organizacionNombre ?? "—"}
              </Text>
            </div>
          </Group>

          <div className="rounded-lg bg-muted p-3">
            <Group justify="space-between" align="center">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="tracking-wide" style={{ fontSize: 10 }}>
                Total{it.monedaCfdi ? ` · ${it.monedaCfdi}` : ""}
              </Text>
              <p className="text-lg font-bold leading-none text-foreground">
                {it.totalCfdi !== null
                  ? formatearMonto(it.totalCfdi, it.monedaCfdi, { minimumFractionDigits: 2 })
                  : "—"}
              </p>
            </Group>
          </div>
        </Stack>
      </div>

      <Divider />

      <Link
        to={`/autorizacion/${it.id}` as never}
        className="flex items-center justify-center gap-1.5 px-4 py-3 text-[13px] font-medium text-multiva-700 transition-colors hover:bg-muted"
      >
        Resolver
        <IconArrowRight size={14} stroke={1.7} />
      </Link>
    </Card>
  );
}
